import { defineMessages, useIntl } from 'react-intl';

import { Chip } from '~/eds/src/components/Chip/Chip';
import { ContentContainer } from '~/eds/src/components/ContentContainer/ContentContainer';
import { Layout } from '~/eds/src/components/Layout/Layout';
import { LayoutDivider } from '~/eds/src/components/Layout/subcomponents/LayoutDivider';
import { Link } from '~/eds/src/components/Link/Link';
import { Text } from '~/eds/src/components/Text/Text';
import { useDocumentGroups } from '~/redux/api/methods/group-nodes/useDocumentGroups';
import { Group } from '~/redux/api/methods/groups/types';
import { useRouting } from '~/routing/useRouting';

const messages = defineMessages({
  itemNameLabel: {
    id: 'GroupMembership.itemNameLabel',
    defaultMessage: 'Item Name',
    description: 'Label for item name field',
  },
  noGroupMembership: {
    id: 'GroupMembership.noGroupMembership',
    defaultMessage: 'Not a member of any groups',
    description: 'Message when item is not a member of any groups',
  },
  currentGroup: {
    id: 'GroupMembership.currentGroup',
    defaultMessage: 'Current Group',
    description: 'Chip label indicating the current group',
  },
});

export interface GroupMembershipProps {
  resourceType: 'document' | 'ticket';
  resourceId: string;
  currentGroup?: string;
}

export interface GroupMembershipListProps {
  groups?: Group[];
  basePath: string;
  currentGroup?: string;
}

export const GroupMembershipList = ({
  groups,
  currentGroup,
  basePath,
}: GroupMembershipListProps) => {
  const { formatMessage } = useIntl();

  if (groups?.length === 0) {
    return (
      <Text color="text.quiet">
        {formatMessage(messages.noGroupMembership)}
      </Text>
    );
  }

  return (
    <LayoutDivider direction="column" pt={4}>
      {groups?.map((group) => (
        <Layout key={group.id} direction="column" py={2}>
          <Layout align="center" spacing={2}>
            <Link pathname={`${basePath}/${group.id}`}>
              {group.attributes.name}
            </Link>
            {currentGroup === group.id && (
              <Chip text={formatMessage(messages.currentGroup)} />
            )}
          </Layout>
          <Text color="text.secondary" variant="body-medium">
            {group.attributes.nodesCount || 0} items
          </Text>
        </Layout>
      ))}
    </LayoutDivider>
  );
};

export const GroupMembership = ({
  resourceType,
  resourceId,
  currentGroup,
}: GroupMembershipProps) => {
  const { location } = useRouting();

  const { data, isFetching } = useDocumentGroups(resourceId, resourceType);

  const isInWorkflowGroups =
    location.pathname.includes('/workflow/groups') ||
    location.pathname.includes('/workflow/tickets');
  const basePath = isInWorkflowGroups
    ? '/workflow/groups'
    : '/documents/groups';

  return (
    <ContentContainer loadingContent={{ isLoading: isFetching }}>
      <Layout direction="column" spacing={2} flex="1">
        <GroupMembershipList
          groups={data?.groups}
          currentGroup={currentGroup}
          basePath={basePath}
        />
      </Layout>
    </ContentContainer>
  );
};
